import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, X, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { apiClient } from '../../services/api';

export type GameAccountRow = {
  id: string;
  gameProviderId: string;
  gameUsername: string;
  provider: { id: string; code: string; name: string } | null;
  capabilities: { canResetPassword: boolean; canChangeUsername: boolean };
};

type ResetMode = 'password' | 'username_and_password';

type SucceededItem = {
  gameProviderId: string;
  providerName?: string;
  username: string;
  password: string;
  emailSent?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
};

function extractError(err: unknown): string {
  const anyErr = err as { message?: string; response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || anyErr?.message || 'Failed to reset game credentials';
}

function capabilityLabel(caps?: { canResetPassword?: boolean; canChangeUsername?: boolean }): string {
  const canPw = caps?.canResetPassword === true;
  const canUser = caps?.canChangeUsername === true;
  if (canPw && canUser) return 'Supports: password + username';
  if (canPw) return 'Supports: password only';
  if (canUser) return 'Supports: username only';
  return 'Reset not supported';
}

function isSelectableForMode(
  caps: { canResetPassword?: boolean; canChangeUsername?: boolean } | undefined,
  mode: ResetMode
): boolean {
  if (mode === 'username_and_password') {
    return caps?.canResetPassword === true && caps?.canChangeUsername === true;
  }
  return caps?.canResetPassword === true;
}

const AdminResetGameCredentialsModal: React.FC<Props> = ({ open, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<GameAccountRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<ResetMode>('password');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<SucceededItem[] | null>(null);
  const [skippedFailed, setSkippedFailed] = useState<Array<{ gameProviderId: string; message: string }>>([]);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAdminUserGameAccounts(userId);
      setAccounts(Array.isArray(res?.data) ? (res.data as GameAccountRow[]) : []);
    } catch (err) {
      setError(extractError(err));
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    setResults(null);
    setSkippedFailed([]);
    setSelected(new Set());
    setMode('password');
    setCopied(false);
    void load();
  }, [open, load]);

  const anyUsernameCapable = useMemo(
    () => accounts.some((a) => isSelectableForMode(a.capabilities, 'username_and_password')),
    [accounts]
  );

  const selectableIds = useMemo(
    () => accounts.filter((a) => isSelectableForMode(a.capabilities, mode)).map((a) => a.gameProviderId),
    [accounts, mode]
  );

  useEffect(() => {
    setSelected((prev) => new Set(Array.from(prev).filter((id) => selectableIds.includes(id))));
  }, [selectableIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleModeChange = (next: ResetMode) => {
    setMode(next);
    setSelected(new Set());
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const ids = Array.from(selected);
    if (ids.length === 0) {
      setError('Select at least one game account');
      return;
    }
    const confirmMsg =
      mode === 'username_and_password'
        ? `Reset username and password for ${ids.length} selected game account(s)? New credentials will be emailed when possible.`
        : `Reset password for ${ids.length} selected game account(s)? New credentials will be emailed when possible.`;
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    setError(null);
    try {
      const body = {
        gameProviderIds: ids,
        mode,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      const res = await apiClient.postAdminUserGameAccountsCredentialsReset(userId, body);
      const data = res?.data;
      setResults(Array.isArray(data?.succeeded) ? data.succeeded : []);
      setSkippedFailed(
        [...(Array.isArray(data?.skipped) ? data.skipped : []), ...(Array.isArray(data?.failed) ? data.failed : [])].map(
          (x: { gameProviderId: string; message?: string; code?: string }) => ({
            gameProviderId: x.gameProviderId,
            message: x.message || x.code || 'Skipped/failed',
          })
        )
      );
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyAll = async () => {
    if (!results?.length) return;
    const text = results
      .map((r) => `${r.providerName || r.gameProviderId}\nUsername: ${r.username}\nPassword: ${r.password}`)
      .join('\n\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 !mt-0 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Card
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-b-none sm:rounded-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0 pr-2">
              <KeyRound className="h-5 w-5 shrink-0" />
              <span className="break-words">Reset game credentials</span>
            </CardTitle>
            <Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={onClose} disabled={submitting}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {results ? (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Copy credentials now — passwords are shown only once. Players also receive an email when available.
              </p>
              {results.length === 0 ? (
                <p className="text-sm text-muted-foreground">No accounts were reset.</p>
              ) : (
                <div className="space-y-3">
                  {results.map((r) => (
                    <div key={r.gameProviderId} className="rounded-md border bg-muted/40 p-3 font-mono text-xs space-y-1">
                      <div className="font-sans font-medium">{r.providerName || r.gameProviderId}</div>
                      <div className="break-all">Username: {r.username}</div>
                      <div className="break-all">Password: {r.password}</div>
                    </div>
                  ))}
                </div>
              )}
              {skippedFailed.length > 0 ? (
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="font-medium">Skipped / failed</div>
                  {skippedFailed.map((s) => (
                    <div key={s.gameProviderId}>
                      {s.gameProviderId}: {s.message}
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {results.length > 0 ? (
                  <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => void copyAll()}>
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied' : 'Copy all'}
                  </Button>
                ) : null}
                <Button type="button" className="w-full sm:w-auto" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Only games that already have an account are listed. Each row shows what this provider supports.
              </p>

              <fieldset className="space-y-2 rounded-md border p-3">
                <legend className="text-sm font-medium px-1">What to reset</legend>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="admin-reset-mode"
                    className="mt-1"
                    checked={mode === 'password'}
                    disabled={submitting}
                    onChange={() => handleModeChange('password')}
                  />
                  <span>
                    <span className="font-medium block">Password only</span>
                    <span className="text-xs text-muted-foreground">Keep the current game username</span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm ${anyUsernameCapable ? 'cursor-pointer' : 'opacity-60'}`}
                  title={
                    anyUsernameCapable
                      ? undefined
                      : 'No listed provider supports in-place username change'
                  }
                >
                  <input
                    type="radio"
                    name="admin-reset-mode"
                    className="mt-1"
                    checked={mode === 'username_and_password'}
                    disabled={submitting || !anyUsernameCapable}
                    onChange={() => handleModeChange('username_and_password')}
                  />
                  <span>
                    <span className="font-medium block">Username + password</span>
                    <span className="text-xs text-muted-foreground">
                      {anyUsernameCapable
                        ? 'Only providers that support both can be selected'
                        : 'Unavailable — no provider here supports username change'}
                    </span>
                  </span>
                </label>
              </fieldset>

              {loading ? (
                <p className="text-sm text-muted-foreground">Loading game accounts…</p>
              ) : accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">Player has no game accounts yet.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-2">
                  <div className="flex justify-between items-center pb-1">
                    <span className="text-xs text-muted-foreground">{accounts.length} account(s)</span>
                    {selectableIds.length > 0 ? (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => setSelected(new Set(selectableIds))}
                      >
                        Select all for this mode
                      </button>
                    ) : null}
                  </div>
                  {accounts.map((a) => {
                    const can = isSelectableForMode(a.capabilities, mode);
                    const label = a.provider?.name || a.provider?.code || a.gameProviderId;
                    return (
                      <label
                        key={a.id}
                        className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm ${can ? 'hover:bg-muted/50' : 'opacity-60'}`}
                        title={can ? undefined : capabilityLabel(a.capabilities)}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          disabled={!can || submitting}
                          checked={selected.has(a.gameProviderId)}
                          onChange={() => toggle(a.gameProviderId)}
                        />
                        <span className="min-w-0">
                          <span className="font-medium block truncate">{label}</span>
                          <span className="text-xs text-muted-foreground font-mono break-all">{a.gameUsername}</span>
                          <span className="block text-xs text-muted-foreground">{capabilityLabel(a.capabilities)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div>
                <label className="text-sm font-medium" htmlFor="admin-game-reset-reason">
                  Reason (optional)
                </label>
                <Input
                  id="admin-game-reset-reason"
                  className="mt-1"
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="Why are you resetting game credentials?"
                  disabled={submitting}
                />
              </div>
              {error ? <p className="text-sm text-destructive break-words">{error}</p> : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
                <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" className="w-full sm:w-auto" disabled={submitting || loading || selected.size === 0}>
                  {submitting ? 'Resetting…' : `Reset selected (${selected.size})`}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminResetGameCredentialsModal;
